import { createContext, ReactNode, useContext, useEffect, useState, useCallback, useMemo } from "react";
import {
  useQuery,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { insertUserSchema, User as SelectUser, InsertUser } from "@shared/schema";
import { getQueryFn, apiRequest, queryClient } from "../lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { signInWithGoogle, checkRedirectResult } from "@/lib/firebase";
import { useMobile } from "@/providers/mobile-provider";
import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";
import pushNotificationService from "@/services/push-notification-service";

const USER_TOKEN_KEY = "user-token";
const USER_DATA_KEY = "user-data";

type AuthResponse = {
  user: SelectUser;
  token: string;
  emailVerificationSent?: boolean;
  requiresVerification?: boolean;
  message?: string;
  isNewUser?: boolean;
};

type AuthContextType = {
  user: SelectUser | null;
  token: string | null;
  isLoading: boolean;
  error: Error | null;
  loginMutation: UseMutationResult<AuthResponse, Error, LoginData>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<AuthResponse, Error, InsertUser>;
  resendVerificationEmail: UseMutationResult<{ message: string, emailVerificationSent: boolean }, Error, void>;
  googleSignIn: UseMutationResult<AuthResponse, Error, void>;
  needsVerification: boolean;
};

type LoginData = Pick<InsertUser, "username" | "password">;

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const { isNative } = useMobile();
  // Move all state hooks to the top level
  const [user, setUser] = useState<SelectUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Get stored token outside of any conditional logic
  const storedToken = localStorage.getItem('token');
  
  // Define all hooks at the top level before any conditional logic
  const {
    data: userData,
    error: queryError,
    isLoading: queryLoading,
    refetch,
  } = useQuery<SelectUser | undefined, Error>({
    queryKey: ["/api/auth/user"],
    queryFn: getQueryFn({ on401: "returnNull" }),
  });

  // Calculate derived state after all hooks
  const needsVerification = !!user && !!user.email && !user.isVerified;

  /**
   * Initialize push notifications for mobile
   */
  const initializePushNotifications = useCallback(() => {
    if (Capacitor.isNativePlatform()) {
      pushNotificationService.initialize().catch(error => {
        console.error("Failed to initialize push notifications:", error);
      });
    }
  }, []);

  /**
   * Deactivate push notifications token
   */
  const deactivatePushToken = useCallback(async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        await pushNotificationService.deactivateToken();
      } catch (error) {
        console.error("Failed to deactivate push token:", error);
      }
    }
  }, []);

  const checkAuth = useCallback(async (): Promise<SelectUser | null> => {
    try {
      const { value: storedToken } = await Preferences.get({
        key: USER_TOKEN_KEY,
      });

      if (storedToken) {
        setToken(storedToken);

        const { value: storedUserData } = await Preferences.get({
          key: USER_DATA_KEY,
        });

        if (storedUserData) {
          const userData = JSON.parse(storedUserData);
          setUser(userData);
        }

        const baseUrl = Capacitor.isNativePlatform()
          ? import.meta.env.VITE_SERVER_URL || ""
          : "";
        
        try {
          const res = await apiRequest("GET", "/api/auth/user");
          if (res.ok) {
            const userData = await res.json();
            
            await Preferences.set({
              key: USER_DATA_KEY,
              value: JSON.stringify(userData),
            });
            
            setUser(userData);
            
            // Initialize push notifications when restoring session
            if (Capacitor.isNativePlatform()) {
              initializePushNotifications();
            }
            
            return userData;
          } else {
            await Preferences.remove({ key: USER_TOKEN_KEY });
            await Preferences.remove({ key: USER_DATA_KEY });
            setToken(null);
            setUser(null);
            return null;
          }
        } catch (error) {
          console.error("Token validation error:", error);
          if (storedUserData) {
            const userData = JSON.parse(storedUserData);
            return userData;
          }
          return null;
        }
      }
      
      setToken(null);
      setUser(null);
      return null;
    } catch (error) {
      console.error("Auth check error:", error);
      setToken(null);
      setUser(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [initializePushNotifications]);

  const login = useCallback(async (credentials: { email: string; password: string }) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const res = await apiRequest("POST", "/api/auth/login", credentials);
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Login failed");
      }
      
      const data = await res.json();
      
      await Preferences.set({
        key: USER_TOKEN_KEY,
        value: data.token,
      });
      
      await Preferences.set({
        key: USER_DATA_KEY,
        value: JSON.stringify(data.user),
      });
      
      setToken(data.token);
      setUser(data.user);
      
      // Initialize push notifications after successful login
      if (Capacitor.isNativePlatform()) {
        initializePushNotifications();
      }
    } catch (error: any) {
      console.error("Login error:", error);
      setError(error.message || "Login failed. Please try again.");
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [initializePushNotifications]);

  const register = useCallback(async (credentials: { username: string; email: string; password: string }) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const res = await apiRequest("POST", "/api/auth/register", credentials);
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Registration failed");
      }
      
      const data = await res.json();
      
      await Preferences.set({
        key: USER_TOKEN_KEY,
        value: data.token,
      });
      
      await Preferences.set({
        key: USER_DATA_KEY,
        value: JSON.stringify(data.user),
      });
      
      setToken(data.token);
      setUser(data.user);
      
      // Initialize push notifications after successful registration
      if (Capacitor.isNativePlatform()) {
        initializePushNotifications();
      }
    } catch (error: any) {
      console.error("Registration error:", error);
      setError(error.message || "Registration failed. Please try again.");
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [initializePushNotifications]);

  const logout = useCallback(async () => {
    setIsLoading(true);
    
    try {
      // Deactivate push token before logging out
      if (Capacitor.isNativePlatform()) {
        await deactivatePushToken();
      }
      
      await apiRequest("POST", "/api/auth/logout");
      
      await Preferences.remove({ key: USER_TOKEN_KEY });
      await Preferences.remove({ key: USER_DATA_KEY });
      
      setToken(null);
      setUser(null);
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [deactivatePushToken]);

  // Define all mutations before any conditional logic
  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginData) => {
      const res = await apiRequest("POST", "/api/auth/login", credentials);
      return await res.json();
    },
    onSuccess: ({ user, token, requiresVerification, message, emailVerificationSent }) => {
      if (user.markedForDelete) {
        localStorage.setItem('token', token);
        queryClient.setQueryData(["/api/auth/user"], user);
        
        toast({
          title: "Account Scheduled for Deletion",
          description: message || "Your account is scheduled for deletion.",
          variant: "destructive",
        });
        
        // Redirect to delete-account page
        window.location.href = "/delete-account";
        return;
      }

      localStorage.setItem('token', token);
      queryClient.setQueryData(["/api/auth/user"], user);
      refetch();
      
      // Initialize push notifications after successful login
      if (Capacitor.isNativePlatform()) {
        initializePushNotifications();
      }
      
      if (requiresVerification && emailVerificationSent) {
        toast({
          title: "Verification Required",
          description: message || "Please check your email to verify your account",
          variant: "default",
        });
      }
      
      window.location.href = '/';
    },
    onError: (error: Error) => {
      toast({
        title: "Login failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  const googleSignIn = useMutation({
    mutationFn: async () => {
      try {
        const result = await signInWithGoogle();
        const idToken = await result.user.getIdToken();
        
        const res = await apiRequest("POST", "/api/auth/google", { idToken });
        return await res.json();
      } catch (error) {
        console.error("Google sign-in error:", error);
        throw new Error("Failed to sign in with Google");
      }
    },
    onSuccess: ({ user, token, isNewUser, message }) => {
      // Check if account is marked for deletion and redirect if needed
      if (user.markedForDelete) {
        localStorage.setItem('token', token);
        queryClient.setQueryData(["/api/auth/user"], user);
        
        toast({
          title: "Account Scheduled for Deletion",
          description: message || "Your account is scheduled for deletion.",
          variant: "destructive",
        });
        
        // Redirect to delete-account page
        window.location.href = "/delete-account";
        return;
      }
      
      localStorage.setItem('token', token);
      queryClient.setQueryData(["/api/auth/user"], user);
      
      // Initialize push notifications after successful Google sign-in
      if (Capacitor.isNativePlatform()) {
        initializePushNotifications();
      }
      
      toast({
        title: isNewUser ? "Account Created" : "Sign-in Successful",
        description: isNewUser 
          ? "Your account has been created with Google" 
          : "You've been signed in with Google",
        variant: "default",
      });
      
      window.location.href = '/';
    },
    onError: (error: Error) => {
      toast({
        title: "Google Sign-in failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (credentials: InsertUser) => {
      const res = await apiRequest("POST", "/api/auth/register", credentials);
      return await res.json();
    },
    onSuccess: ({ user, token, emailVerificationSent, requiresVerification }: AuthResponse) => {
      localStorage.setItem('token', token);
      queryClient.setQueryData(["/api/auth/user"], user);
      
      // Initialize push notifications after successful registration
      if (Capacitor.isNativePlatform()) {
        initializePushNotifications();
      }
      
      if (emailVerificationSent && requiresVerification) {
        toast({
          title: "Registration Successful",
          description: "Please check your email to verify your account",
          variant: "default",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Registration failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      // Deactivate push token before logging out
      if (Capacitor.isNativePlatform()) {
        await deactivatePushToken();
      }
      
      localStorage.removeItem('token');
      await apiRequest("POST", "/api/auth/logout");
      queryClient.setQueryData(["/api/auth/user"], null);
      queryClient.clear();
    },
    onSuccess: () => {
      window.location.href = '/auth';
    },
    onError: (error: Error) => {
      toast({
        title: "Logout failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  const resendVerificationEmail = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/auth/resend-verification");
      return await res.json();
    },
    onSuccess: (data) => {
      if (data.emailVerificationSent) {
        toast({
          title: "Verification Email Sent",
          description: "Please check your email inbox to verify your account",
          variant: "default",
        });
      } else {
        toast({
          title: "Couldn't Send Verification Email",
          description: "There was a problem sending the verification email",
          variant: "destructive",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Verification Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Use memo to create the context value after all hooks and state
  const contextValue = useMemo(
    () => ({
      user,
      token,
      login,
      register,
      logout,
      isLoading,
      error,
      checkAuth,
    }),
    [user, token, login, register, logout, isLoading, error, checkAuth]
  );

  // Always call useEffect after all other hooks
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (isNative) {
      checkRedirectResult().then(async (result) => {
        if (result && result.user) {
          try {
            const idToken = await result.user.getIdToken();
            const res = await apiRequest("POST", "/api/auth/google", { idToken });
            const data = await res.json();
            
            localStorage.setItem('token', data.token);
            queryClient.setQueryData(["/api/auth/user"], data.user);
            
            // Check if account is marked for deletion and redirect if needed
            if (data.markedForDelete) {
              toast({
                title: "Account Scheduled for Deletion",
                description: data.message || "Your account is scheduled for deletion.",
                variant: "destructive",
              });
              
              window.location.href = "/delete-account";
              return;
            }
            
            refetch();
            
            // Initialize push notifications after successful Google auth redirect
            if (Capacitor.isNativePlatform()) {
              initializePushNotifications();
            }
            
            toast({
              title: data.isNewUser ? "Account Created" : "Sign-in Successful",
              description: data.isNewUser 
                ? "Your account has been created with Google" 
                : "You've been signed in with Google",
              variant: "default",
            });
            
            window.location.href = '/';
          } catch (error) {
            console.error("Error processing Google auth redirect:", error);
          }
        }
      }).catch(error => {
        console.error("Error checking for redirect result:", error);
      });
    }
  }, [isNative, toast, refetch, initializePushNotifications]);

  return (
    <AuthContext.Provider
      value={{
        user: userData ?? null,
        token: storedToken,
        isLoading,
        error,
        loginMutation,
        logoutMutation,
        registerMutation,
        resendVerificationEmail,
        googleSignIn,
        needsVerification
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}